import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { collection, query, where, getDocs, doc, updateDoc, writeBatch, getDoc, collectionGroup } from 'firebase/firestore';
import { db, upsertUsersRootProfile } from '../../../utils/firebase';
import Card from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import CheckCircleIcon from '../../../components/icons/CheckCircleIcon';
import CloseCircleIcon from '../../../components/icons/CloseCircleIcon';

interface TeacherRequest {
  id: string;
  userId: string;
  name: string;
  email: string;
  schoolId: string;
  schoolCode: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: Date;
  updatedAt: Date;
}

// 관리자 권한 확인 함수 추가
const checkAdminRole = async (userId: string) => {
  try {
    // superAdmin 확인
    const superAdminQuery = query(
      collectionGroup(db, 'teachers'),
      where('uid', '==', userId),
      where('role', '==', 'superAdmin')
    );
    const superAdminDocs = await getDocs(superAdminQuery);
    if (!superAdminDocs.empty) {
      return { 
        role: 'superAdmin',
        schoolId: superAdminDocs.docs[0].data().schoolId 
      };
    }

    // schoolAdmin 확인
    const schoolAdminQuery = query(
      collectionGroup(db, 'teachers'),
      where('uid', '==', userId),
      where('role', '==', 'schoolAdmin')
    );
    const schoolAdminDocs = await getDocs(schoolAdminQuery);
    if (!schoolAdminDocs.empty) {
      return { 
        role: 'schoolAdmin',
        schoolId: schoolAdminDocs.docs[0].data().schoolId 
      };
    }

    return { role: 'none' };
  } catch (error) {
    console.error('Error checking admin role:', error);
    return { role: 'none' };
  }
};

const TeacherApproval: React.FC = () => {
  const [requests, setRequests] = useState<TeacherRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [adminInfo, setAdminInfo] = useState<{ role: string; schoolId?: string }>({ role: 'none' });
  const { currentUser } = useAuth();

  useEffect(() => {
    const initializeAdminInfo = async () => {
      if (currentUser) {
        const info = await checkAdminRole(currentUser.uid);
        setAdminInfo(info);
      }
    };
    initializeAdminInfo();
  }, [currentUser]);

  useEffect(() => {
    if (adminInfo.role !== 'none') {
      fetchRequests();
    }
  }, [currentUser, tabValue, adminInfo]);

  const fetchRequests = async () => {
    try {
      if (!currentUser) return;

      const requestsRef = collection(db, 'teacher_requests');
      let q;

      // 기본 상태 필터
      const statusFilter = tabValue === 0 ? 'pending' : tabValue === 1 ? 'approved' : 'rejected';

      if (adminInfo.role === 'superAdmin') {
        // superAdmin은 모든 요청을 볼 수 있음
        q = query(requestsRef, where('status', '==', statusFilter));
      } else if (adminInfo.role === 'schoolAdmin' && adminInfo.schoolId) {
        // schoolAdmin은 자신의 학교 요청만 볼 수 있음
        q = query(
          requestsRef,
          where('status', '==', statusFilter),
          where('schoolId', '==', adminInfo.schoolId)
        );
      } else {
        setError('접근 권한이 없습니다.');
        setRequests([]);
        return;
      }

      const querySnapshot = await getDocs(q);
      const requestList: TeacherRequest[] = [];

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        
        // 날짜 데이터 안전하게 처리
        let createdAt = new Date();
        let updatedAt = new Date();
        
        if (data.createdAt) {
          if (typeof data.createdAt.toDate === 'function') {
            createdAt = data.createdAt.toDate();
          } else if (data.createdAt instanceof Date) {
            createdAt = data.createdAt;
          } else if (typeof data.createdAt === 'string') {
            createdAt = new Date(data.createdAt);
          }
        }
        
        if (data.updatedAt) {
          if (typeof data.updatedAt.toDate === 'function') {
            updatedAt = data.updatedAt.toDate();
          } else if (data.updatedAt instanceof Date) {
            updatedAt = data.updatedAt;
          } else if (typeof data.updatedAt === 'string') {
            updatedAt = new Date(data.updatedAt);
          }
        }

        requestList.push({
          id: doc.id,
          userId: data.userId,
          name: data.name,
          email: data.email,
          schoolId: data.schoolId,
          schoolCode: data.schoolCode,
          status: data.status,
          createdAt,
          updatedAt,
        });
      });

      setRequests(requestList);
    } catch (err: any) {
      setError('승인 요청 목록을 불러오는데 실패했습니다.');
      console.error('Error fetching requests:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId: string) => {
    try {
      const batch = writeBatch(db);
      
      // 승인 요청 문서 가져오기
      const requestRef = doc(db, 'teacher_requests', requestId);
      const requestDoc = await getDoc(requestRef);
      const requestData = requestDoc.data();

      if (!requestData) {
        throw new Error('승인 요청 데이터를 찾을 수 없습니다.');
      }

      const approvedUid = (requestData.uid as string | undefined) || (requestData.userId as string | undefined) || requestId;

      // teachers 컬렉션에 새 문서 생성
      const teacherRef = doc(db, `schools/${requestData.schoolId}/teachers`, approvedUid);
      batch.set(teacherRef, {
        uid: approvedUid,
        id: approvedUid,
        email: requestData.email,
        name: requestData.name,
        role: 'teacher',
        schoolId: requestData.schoolId,
        subject: '',
        stampClasses: [],
        classRoles: [],
        isActive: true,
        plan: 'free',
        createdAt: new Date(),
        updatedAt: new Date()
      });

      // 승인 요청 상태 업데이트
      batch.update(requestRef, {
        status: 'approved',
        updatedAt: new Date()
      });

      await batch.commit();

      await upsertUsersRootProfile({
        uid: approvedUid,
        email: requestData.email || '',
        name: requestData.name || '',
        role: 'teacher',
        schoolId: requestData.schoolId,
        loginType: (requestData.loginType as string | undefined) || 'local',
        isActive: true,
      });

      // 승인된 요청을 목록에서 제거
      setRequests((prev) => prev.filter((request) => request.id !== requestId));
    } catch (err: any) {
      setError('교사 승인에 실패했습니다.');
      console.error('Error approving teacher:', err);
    }
  };

  const handleReject = async (requestId: string) => {
    if (!window.confirm('정말로 이 요청을 거절하시겠습니까?')) {
      return;
    }

    try {
      const requestRef = doc(db, 'teacher_requests', requestId);
      await updateDoc(requestRef, {
        status: 'rejected',
        updatedAt: new Date()
      });

      // 거절된 요청을 목록에서 제거
      setRequests((prev) => prev.filter((request) => request.id !== requestId));
    } catch (err: any) {
      setError('요청 거절에 실패했습니다.');
      console.error('Error rejecting request:', err);
    }
  };

  const handleTabChange = (newValue: number) => {
    setTabValue(newValue);
  };

  const tabs = [
    { label: '승인 대기', value: 0 },
    { label: '승인 완료', value: 1 },
    { label: '승인 거절', value: 2 }
  ];

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '200px' 
      }}>
        <div style={{ fontSize: '16px', color: 'var(--Color-text-normal, #161616)' }}>
          로딩 중...
        </div>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* 헤더 */}
      <div
        style={{
          fontSize: '24px',
          fontWeight: 600,
          lineHeight: '36px',
          color: 'var(--Color-text-strong, #0D0D0D)'
        }}
      >
        교사 승인 관리
      </div>

      {/* 에러 메시지 */}
      {error && (
        <Card
          padding="12px 16px"
          background="#FFF5F5"
          style={{ border: '1px solid #FFE0E0' }}
        >
          <div style={{ fontSize: '14px', color: '#FF3838' }}>{error}</div>
        </Card>
      )}

      {/* 탭 */}
      <div style={{ display: 'flex', gap: '8px', borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)' }}>
        {tabs.map((tab) => (
          <button
            key={tab.value}
            type="button"
            onClick={() => handleTabChange(tab.value)}
            style={{
              padding: '12px 20px',
              border: 'none',
              backgroundColor: 'transparent',
              color: tabValue === tab.value 
                ? 'var(--Color-fill-brand-color, #FF6F0F)' 
                : 'var(--Color-text-normal, #161616)',
              fontSize: '14px',
              fontWeight: tabValue === tab.value ? 600 : 500,
              cursor: 'pointer',
              borderBottom: tabValue === tab.value 
                ? '2px solid var(--Color-fill-brand-color, #FF6F0F)' 
                : '2px solid transparent',
              transition: 'all 0.2s ease',
              marginBottom: '-1px'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* 탭 패널 - 승인 대기 */}
      {tabValue === 0 && (
        <div>
          {requests.length === 0 ? (
            <Card padding="40px" style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '14px', color: 'var(--Color-text-normal, #161616)' }}>
                승인 대기 중인 교사가 없습니다.
              </div>
            </Card>
          ) : (
            <Card padding="0" style={{ overflow: 'hidden', width: '100%' }}>
              <div style={{ overflowX: 'auto', width: '100%' }}>
                <table style={{ width: '100%', minWidth: '800px', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: 'var(--Color-fill-hover, #F5F5F5)' }}>
                      <th style={{
                        padding: '12px 20px',
                        textAlign: 'left',
                        fontSize: '14px',
                        fontWeight: 600,
                        color: 'var(--Color-text-strong, #0D0D0D)',
                        borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                        whiteSpace: 'nowrap'
                      }}>
                        이름
                      </th>
                      <th style={{
                        padding: '12px 20px',
                        textAlign: 'left',
                        fontSize: '14px',
                        fontWeight: 600,
                        color: 'var(--Color-text-strong, #0D0D0D)',
                        borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                        minWidth: '200px'
                      }}>
                        이메일
                      </th>
                      <th style={{
                        padding: '12px 20px',
                        textAlign: 'left',
                        fontSize: '14px',
                        fontWeight: 600,
                        color: 'var(--Color-text-strong, #0D0D0D)',
                        borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                        whiteSpace: 'nowrap'
                      }}>
                        학교 코드
                      </th>
                      <th style={{
                        padding: '12px 20px',
                        textAlign: 'left',
                        fontSize: '14px',
                        fontWeight: 600,
                        color: 'var(--Color-text-strong, #0D0D0D)',
                        borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                        whiteSpace: 'nowrap'
                      }}>
                        요청일
                      </th>
                      <th style={{
                        padding: '12px 20px',
                        textAlign: 'right',
                        fontSize: '14px',
                        fontWeight: 600,
                        color: 'var(--Color-text-strong, #0D0D0D)',
                        borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                        whiteSpace: 'nowrap'
                      }}>
                        작업
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {requests.map((request) => (
                      <tr key={request.id} style={{
                        borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)'
                      }}>
                        <td style={{
                          padding: '12px 20px',
                          fontSize: '14px',
                          color: 'var(--Color-text-normal, #161616)',
                          whiteSpace: 'nowrap'
                        }}>
                          {request.name}
                        </td>
                        <td style={{
                          padding: '12px 20px',
                          fontSize: '14px',
                          color: 'var(--Color-text-normal, #161616)',
                          minWidth: '200px'
                        }}>
                          {request.email}
                        </td>
                        <td style={{
                          padding: '12px 20px',
                          fontSize: '14px',
                          color: 'var(--Color-text-normal, #161616)',
                          whiteSpace: 'nowrap'
                        }}>
                          {request.schoolCode}
                        </td>
                        <td style={{
                          padding: '12px 20px',
                          fontSize: '14px',
                          color: 'var(--Color-text-normal, #161616)',
                          whiteSpace: 'nowrap'
                        }}>
                          {request.createdAt.toLocaleDateString('ko-KR')}
                        </td>
                        <td style={{
                          padding: '12px 20px',
                          textAlign: 'right',
                          whiteSpace: 'nowrap'
                        }}>
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                            <Button
                              size="small"
                              variant="primary"
                              onClick={() => handleApprove(request.id)}
                            >
                              승인
                            </Button>
                            <Button
                              size="small"
                              variant="cancel"
                              onClick={() => handleReject(request.id)}
                            >
                              거절
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* 탭 패널 - 승인 완료 */}
      {tabValue === 1 && (
        <div>
          {requests.length === 0 ? (
            <Card padding="40px" style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '14px', color: 'var(--Color-text-normal, #161616)' }}>
                승인된 교사가 없습니다.
              </div>
            </Card>
          ) : (
            <Card padding="0" style={{ overflow: 'hidden', width: '100%' }}>
              <div style={{ overflowX: 'auto', width: '100%' }}>
                <table style={{ width: '100%', minWidth: '800px', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: 'var(--Color-fill-hover, #F5F5F5)' }}>
                      <th style={{
                        padding: '12px 20px',
                        textAlign: 'left',
                        fontSize: '14px',
                        fontWeight: 600,
                        color: 'var(--Color-text-strong, #0D0D0D)',
                        borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                        whiteSpace: 'nowrap'
                      }}>
                        이름
                      </th>
                      <th style={{
                        padding: '12px 20px',
                        textAlign: 'left',
                        fontSize: '14px',
                        fontWeight: 600,
                        color: 'var(--Color-text-strong, #0D0D0D)',
                        borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                        minWidth: '200px'
                      }}>
                        이메일
                      </th>
                      <th style={{
                        padding: '12px 20px',
                        textAlign: 'left',
                        fontSize: '14px',
                        fontWeight: 600,
                        color: 'var(--Color-text-strong, #0D0D0D)',
                        borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                        whiteSpace: 'nowrap'
                      }}>
                        학교 코드
                      </th>
                      <th style={{
                        padding: '12px 20px',
                        textAlign: 'left',
                        fontSize: '14px',
                        fontWeight: 600,
                        color: 'var(--Color-text-strong, #0D0D0D)',
                        borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                        whiteSpace: 'nowrap'
                      }}>
                        요청일
                      </th>
                      <th style={{
                        padding: '12px 20px',
                        textAlign: 'left',
                        fontSize: '14px',
                        fontWeight: 600,
                        color: 'var(--Color-text-strong, #0D0D0D)',
                        borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                        whiteSpace: 'nowrap'
                      }}>
                        승인일
                      </th>
                      <th style={{
                        padding: '12px 20px',
                        textAlign: 'left',
                        fontSize: '14px',
                        fontWeight: 600,
                        color: 'var(--Color-text-strong, #0D0D0D)',
                        borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                        whiteSpace: 'nowrap'
                      }}>
                        상태
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {requests.map((request) => (
                      <tr key={request.id} style={{
                        borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)'
                      }}>
                        <td style={{
                          padding: '12px 20px',
                          fontSize: '14px',
                          color: 'var(--Color-text-normal, #161616)',
                          whiteSpace: 'nowrap'
                        }}>
                          {request.name}
                        </td>
                        <td style={{
                          padding: '12px 20px',
                          fontSize: '14px',
                          color: 'var(--Color-text-normal, #161616)',
                          minWidth: '200px'
                        }}>
                          {request.email}
                        </td>
                        <td style={{
                          padding: '12px 20px',
                          fontSize: '14px',
                          color: 'var(--Color-text-normal, #161616)',
                          whiteSpace: 'nowrap'
                        }}>
                          {request.schoolCode}
                        </td>
                        <td style={{
                          padding: '12px 20px',
                          fontSize: '14px',
                          color: 'var(--Color-text-normal, #161616)',
                          whiteSpace: 'nowrap'
                        }}>
                          {request.createdAt.toLocaleDateString('ko-KR')}
                        </td>
                        <td style={{
                          padding: '12px 20px',
                          fontSize: '14px',
                          color: 'var(--Color-text-normal, #161616)',
                          whiteSpace: 'nowrap'
                        }}>
                          {request.updatedAt.toLocaleDateString('ko-KR')}
                        </td>
                        <td style={{
                          padding: '12px 20px',
                          fontSize: '14px',
                          color: 'var(--Color-text-normal, #161616)',
                          whiteSpace: 'nowrap'
                        }}>
                          <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '4px 12px',
                            borderRadius: '12px',
                            backgroundColor: '#F0FFF4',
                            color: '#38A169',
                            fontSize: '12px',
                            fontWeight: 500
                          }}>
                            <CheckCircleIcon width={14} height={14} color="#38A169" />
                            승인됨
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>
      )}

      {/* 탭 패널 - 승인 거절 */}
      {tabValue === 2 && (
        <div>
          {requests.length === 0 ? (
            <Card padding="40px" style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '14px', color: 'var(--Color-text-normal, #161616)' }}>
                거절된 요청이 없습니다.
              </div>
            </Card>
          ) : (
            <Card padding="0" style={{ overflow: 'hidden', width: '100%' }}>
              <div style={{ overflowX: 'auto', width: '100%' }}>
                <table style={{ width: '100%', minWidth: '800px', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: 'var(--Color-fill-hover, #F5F5F5)' }}>
                      <th style={{
                        padding: '12px 20px',
                        textAlign: 'left',
                        fontSize: '14px',
                        fontWeight: 600,
                        color: 'var(--Color-text-strong, #0D0D0D)',
                        borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                        whiteSpace: 'nowrap'
                      }}>
                        이름
                      </th>
                      <th style={{
                        padding: '12px 20px',
                        textAlign: 'left',
                        fontSize: '14px',
                        fontWeight: 600,
                        color: 'var(--Color-text-strong, #0D0D0D)',
                        borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                        minWidth: '200px'
                      }}>
                        이메일
                      </th>
                      <th style={{
                        padding: '12px 20px',
                        textAlign: 'left',
                        fontSize: '14px',
                        fontWeight: 600,
                        color: 'var(--Color-text-strong, #0D0D0D)',
                        borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                        whiteSpace: 'nowrap'
                      }}>
                        학교 코드
                      </th>
                      <th style={{
                        padding: '12px 20px',
                        textAlign: 'left',
                        fontSize: '14px',
                        fontWeight: 600,
                        color: 'var(--Color-text-strong, #0D0D0D)',
                        borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                        whiteSpace: 'nowrap'
                      }}>
                        요청일
                      </th>
                      <th style={{
                        padding: '12px 20px',
                        textAlign: 'left',
                        fontSize: '14px',
                        fontWeight: 600,
                        color: 'var(--Color-text-strong, #0D0D0D)',
                        borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                        whiteSpace: 'nowrap'
                      }}>
                        거절일
                      </th>
                      <th style={{
                        padding: '12px 20px',
                        textAlign: 'left',
                        fontSize: '14px',
                        fontWeight: 600,
                        color: 'var(--Color-text-strong, #0D0D0D)',
                        borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)',
                        whiteSpace: 'nowrap'
                      }}>
                        상태
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {requests.map((request) => (
                      <tr key={request.id} style={{
                        borderBottom: '1px solid var(--Color-line-normal, #F0F0F0)'
                      }}>
                        <td style={{
                          padding: '12px 20px',
                          fontSize: '14px',
                          color: 'var(--Color-text-normal, #161616)',
                          whiteSpace: 'nowrap'
                        }}>
                          {request.name}
                        </td>
                        <td style={{
                          padding: '12px 20px',
                          fontSize: '14px',
                          color: 'var(--Color-text-normal, #161616)',
                          minWidth: '200px'
                        }}>
                          {request.email}
                        </td>
                        <td style={{
                          padding: '12px 20px',
                          fontSize: '14px',
                          color: 'var(--Color-text-normal, #161616)',
                          whiteSpace: 'nowrap'
                        }}>
                          {request.schoolCode}
                        </td>
                        <td style={{
                          padding: '12px 20px',
                          fontSize: '14px',
                          color: 'var(--Color-text-normal, #161616)',
                          whiteSpace: 'nowrap'
                        }}>
                          {request.createdAt.toLocaleDateString('ko-KR')}
                        </td>
                        <td style={{
                          padding: '12px 20px',
                          fontSize: '14px',
                          color: 'var(--Color-text-normal, #161616)',
                          whiteSpace: 'nowrap'
                        }}>
                          {request.updatedAt.toLocaleDateString('ko-KR')}
                        </td>
                        <td style={{
                          padding: '12px 20px',
                          fontSize: '14px',
                          color: 'var(--Color-text-normal, #161616)',
                          whiteSpace: 'nowrap'
                        }}>
                          <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '4px 12px',
                            borderRadius: '12px',
                            backgroundColor: '#FFF5F5',
                            color: '#FF3838',
                            fontSize: '12px',
                            fontWeight: 500
                          }}>
                            <CloseCircleIcon width={14} height={14} color="#FF3838" />
                            거절됨
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default TeacherApproval;
